import { useState } from "react";
import { RestaurantInfoButton } from "@/components/restaurant/RestaurantInfoButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { User, Store, Bike, Scissors, ArrowLeft, CheckCircle, Mail, Phone } from "lucide-react";

import ArteknosLogo from "@/components/ArteknosLogo";
import { ArtisanSpecialtyPicker } from "@/components/ArtisanSpecialtyPicker";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des types d'inscription — palette luxury unifiée
const accountTypes = [
  {
    id: "client",
    label: "Client(e)",
    description: "Commander des livraisons",
    icon: User,
    iconColor: "text-blue-400",
    features: ["Commander des livraisons", "Suivre vos colis en temps réel", "Paiement en ligne ou à la livraison"]
  },
  {
    id: "partner",
    label: "Partenaire / Commerçant",
    description: "Vendre et expédier vos produits",
    icon: Store,
    iconColor: "text-emerald-400",
    features: ["Gérer vos commandes", "Livreurs assignés automatiquement", "Tableau de bord dédié"]
  },
  {
    id: "courier",
    label: "Livreur / Chauffeur",
    description: "Effectuer des livraisons et gagner",
    icon: Bike,
    iconColor: "text-orange-400",
    features: ["Accepter des courses", "Suivi GPS en temps réel", "Portefeuille et gains"]
  },
  {
    id: "vendor",
    label: "Artisans",
    description: "Gérer rendez-vous et livraisons",
    icon: Scissors,
    iconColor: "text-[#D4AF37]",
    features: ["Agenda de rendez-vous", "Livraison des confections", "Gestion des clients"]
  }
];

// Classes communes pour les inputs dark/luxury
const INPUT_CLASS = "h-12 bg-[#0F172A] border border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-[#D4AF37]/40 focus-visible:border-[#D4AF37]";
const SELECT_CLASS = "h-12 w-full rounded-lg bg-[#0F172A] border border-white/10 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40 focus:border-[#D4AF37]";

export default function AuthPage({ onLogin }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedRole = searchParams.get('role');

  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState(preselectedRole || null);
  const [showRegistrationForm, setShowRegistrationForm] = useState(!!preselectedRole);
  const [loginMethod, setLoginMethod] = useState("email");
  const [registerMethod, setRegisterMethod] = useState("email");

  const [loginData, setLoginData] = useState({ identifier: "", password: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    full_name: "",
    role: preselectedRole || "client",
    phone: "",
    address: "",
    vehicle_type: "",
    driver_type: "livreur",  // livreur (moto/velo/voiture pour colis) | taxi (voiture transport personnes)
    business_name: "",
    business_type: ""
  });
  const [artisanSpecs, setArtisanSpecs] = useState([]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        identifier: loginData.identifier,
        password: loginData.password
      });
      toast.success("Connexion réussie!");
      onLogin(response.data.user, response.data.access_token);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              await axios.post(`${API}/gps/user-location`, {
                user_id: response.data.user.id,
                latitude: position.coords.latitude,
                longitude: position.coords.longitude
              });
            } catch (e) {
              console.warn('GPS save failed', e);
            }
          }, () => {}, { enableHighAccuracy: true, timeout: 10000 }
        );
      }
      const role = response.data.user.role;
      const redirect = sessionStorage.getItem('post_login_redirect');
      if (redirect) {
        sessionStorage.removeItem('post_login_redirect');
        navigate(redirect);
      } else if (role === 'courier') {
        navigate('/courier');
      } else if (role === 'partner') {
        navigate('/partner');
      } else if (role === 'vendor') {
        navigate('/vendor');
      } else if (role === 'artisan') {
        navigate('/artisans/dashboard');
      } else {
        navigate('/home');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Identifiants invalides");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const dataToSend = { ...registerData };
    if (registerMethod === "phone") {
      dataToSend.email = null;
    }

    if (registerMethod === "phone" && !dataToSend.phone) {
      toast.error("Numéro de téléphone requis");
      setIsLoading(false);
      return;
    }
    if (registerMethod === "email" && !dataToSend.email) {
      toast.error("Email requis");
      setIsLoading(false);
      return;
    }

    if (registerData.role === "artisan" && artisanSpecs.length === 0) {
      toast.error("Choisissez au moins un métier");
      setIsLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/auth/register`, {
        ...dataToSend,
        ...(registerData.role === "artisan" ? { specialties: artisanSpecs } : {}),
      });
      if (registerData.role === 'courier') {
        toast.success("Inscription réussie! Votre compte livreur/chauffeur est actif, connectez-vous pour recevoir des courses.");
      } else {
        toast.success("Inscription réussie! Connectez-vous maintenant.");
      }
      sessionStorage.setItem('just_registered', 'true');
      sessionStorage.setItem('registered_role', registerData.role);
      sessionStorage.setItem('registered_name', registerData.full_name);

      setLoginData({
        identifier: registerMethod === "phone" ? registerData.phone : registerData.email,
        password: registerData.password
      });
      setShowRegistrationForm(false);
      setSelectedAccountType(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur d'inscription");
    } finally {
      setIsLoading(false);
    }
  };

  const selectAccountType = (typeId) => {
    setSelectedAccountType(typeId);
    setRegisterData({ ...registerData, role: typeId });
  };

  const proceedToForm = () => {
    if (selectedAccountType) {
      setShowRegistrationForm(true);
    }
  };

  const selectedType = accountTypes.find(t => t.id === selectedAccountType);

  // Toggle pills (email / phone) — style luxury
  const renderMethodToggle = (method, setMethod, testIdPrefix) => (
    <div className="flex gap-2 p-1 bg-[#0F172A] border border-white/10 rounded-full mb-4">
      <button
        type="button"
        onClick={() => setMethod("email")}
        data-testid={`${testIdPrefix}-method-email`}
        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-full text-sm font-semibold transition-all ${
          method === "email"
            ? "bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] shadow-md"
            : "text-gray-400 hover:text-white"
        }`}
      >
        <Mail className="w-4 h-4" />
        Email
      </button>
      <button
        type="button"
        onClick={() => setMethod("phone")}
        data-testid={`${testIdPrefix}-method-phone`}
        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-full text-sm font-semibold transition-all ${
          method === "phone"
            ? "bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] shadow-md"
            : "text-gray-400 hover:text-white"
        }`}
      >
        <Phone className="w-4 h-4" />
        Téléphone
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 py-8" data-testid="auth-page">
      {/* Halo doré subtil en arrière-plan */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl"></div>
        <div className="absolute -bottom-40 -right-40 w-96 h-96 rounded-full bg-[#003594]/20 blur-3xl"></div>
      </div>

      <Card className="relative w-full max-w-2xl shadow-2xl border border-white/5 bg-[#1E293B] text-white rounded-[2.5rem]">
        <CardHeader className="text-center pb-2 pt-8">
          <div className="mx-auto mb-4">
            <ArteknosLogo size="xl" />
          </div>
          <CardTitle className="text-2xl font-black uppercase tracking-tight text-white" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="auth-title">
            WÔMA de ARTEKNOS
          </CardTitle>
          <CardDescription className="text-[10px] text-gray-400 font-bold uppercase tracking-[0.25em] mt-1">
            Dakar, Sénégal · 🇸🇳
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-4 pb-8">
          <Tabs defaultValue={preselectedRole ? "register" : "login"} className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-[#0F172A] border border-white/10 rounded-full p-1 h-auto">
              <TabsTrigger
                value="login"
                className="text-sm py-3 font-bold uppercase tracking-widest rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#D4AF37] data-[state=active]:to-[#B8962E] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-md text-gray-400"
                data-testid="tab-login"
              >
                Connexion
              </TabsTrigger>
              <TabsTrigger
                value="register"
                className="text-sm py-3 font-bold uppercase tracking-widest rounded-full data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#D4AF37] data-[state=active]:to-[#B8962E] data-[state=active]:text-[#0F172A] data-[state=active]:shadow-md text-gray-400"
                data-testid="tab-register"
                onClick={() => { setShowRegistrationForm(false); setSelectedAccountType(null); }}
              >
                Inscription
              </TabsTrigger>
            </TabsList>

            {/* LOGIN TAB */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                {renderMethodToggle(loginMethod, setLoginMethod, "login")}

                <div className="space-y-2">
                  <Label htmlFor="login-identifier" className="text-gray-300 text-xs uppercase tracking-widest font-bold">
                    {loginMethod === "email" ? "Email" : "Numéro de téléphone"}
                  </Label>
                  <Input
                    id="login-identifier"
                    type={loginMethod === "email" ? "email" : "tel"}
                    placeholder={loginMethod === "email" ? "votre@email.com" : "+221 77 123 45 67"}
                    value={loginData.identifier}
                    onChange={(e) => setLoginData({ ...loginData, identifier: e.target.value })}
                    required
                    className={INPUT_CLASS}
                    data-testid="login-identifier-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Mot de passe</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    className={INPUT_CLASS}
                    data-testid="login-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] hover:opacity-90 text-[#0F172A] rounded-full py-6 text-sm font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/20"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? "Connexion..." : "Se connecter"}
                </Button>
              </form>
            </TabsContent>

            {/* REGISTER TAB */}
            <TabsContent value="register">
              {!showRegistrationForm ? (
                /* STEP 1 — Choose account type */
                <div className="space-y-6">
                  <div className="text-center">
                    <h3 className="text-xl font-black uppercase tracking-tight text-white mb-2">Choisissez votre type de compte</h3>
                    <p className="text-gray-400 text-xs uppercase tracking-widest">Sélectionnez le profil qui correspond à votre activité</p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {accountTypes.map((type) => {
                      const Icon = type.icon;
                      const isSelected = selectedAccountType === type.id;

                      return (
                        <div
                          key={type.id}
                          onClick={() => selectAccountType(type.id)}
                          className={`relative cursor-pointer rounded-[2rem] p-5 transition-all duration-200 bg-[#0F172A] border ${
                            isSelected
                              ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/40 shadow-xl shadow-[#D4AF37]/10"
                              : "border-white/10 hover:border-[#D4AF37]/50"
                          }`}
                          data-testid={`account-type-${type.id}`}
                        >
                          {isSelected && (
                            <div className="absolute top-3 right-3">
                              <CheckCircle className="w-6 h-6 text-[#D4AF37]" />
                            </div>
                          )}

                          <div className="flex items-start gap-3">
                            <div className={`w-14 h-14 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 ${type.iconColor}`}>
                              <Icon className="w-7 h-7" />
                            </div>
                            <div className="flex-1">
                              <h4 className="font-black text-sm uppercase tracking-tight text-white">{type.label}</h4>
                              <p className="text-xs text-gray-400 mb-2">{type.description}</p>
                              <ul className="space-y-1">
                                {type.features.map((feature) => (
                                  <li key={feature} className="text-[11px] text-gray-500 flex items-center gap-1.5">
                                    <span className="w-1 h-1 bg-[#D4AF37] rounded-full"></span>
                                    {feature}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    onClick={proceedToForm}
                    disabled={!selectedAccountType}
                    className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] hover:opacity-90 disabled:opacity-40 rounded-full py-6 text-sm font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/20"
                    data-testid="proceed-to-form-btn"
                  >
                    {selectedAccountType
                      ? `Continuer comme ${selectedType?.label}`
                      : "Sélectionnez un type de compte"}
                  </Button>
                </div>
              ) : (
                /* STEP 2 — Registration form */
                <div className="space-y-4">
                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowRegistrationForm(false)}
                      className="rounded-full text-gray-300 hover:text-white hover:bg-white/5"
                      data-testid="back-to-selection-btn"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Retour
                    </Button>
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0F172A] border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-bold uppercase tracking-widest">
                      {selectedType && <selectedType.icon className="w-4 h-4" />}
                      <span>Inscription {selectedType?.label}</span>
                    </div>
                  </div>

                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label className="text-gray-300 text-xs uppercase tracking-widest font-bold mb-2 block">S&apos;inscrire avec</Label>
                      {renderMethodToggle(registerMethod, setRegisterMethod, "register")}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="register-name" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Nom complet *</Label>
                        <Input
                          id="register-name"
                          placeholder="Mamadou Diallo"
                          value={registerData.full_name}
                          onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                          required
                          className={INPUT_CLASS}
                          data-testid="register-name-input"
                        />
                      </div>

                      {registerMethod === "phone" ? (
                        <div className="space-y-2">
                          <Label htmlFor="register-phone" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Numéro de téléphone *</Label>
                          <Input
                            id="register-phone"
                            type="tel"
                            placeholder="+221 77 123 45 67"
                            value={registerData.phone}
                            onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                            required
                            className={INPUT_CLASS}
                            data-testid="register-phone-input"
                          />
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Label htmlFor="register-email" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Email *</Label>
                          <Input
                            id="register-email"
                            type="email"
                            placeholder="votre@email.com"
                            value={registerData.email}
                            onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                            required
                            className={INPUT_CLASS}
                            data-testid="register-email-input"
                          />
                        </div>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={registerMethod === "phone" ? "register-email-optional" : "register-phone-optional"} className="text-gray-300 text-xs uppercase tracking-widest font-bold">
                        {registerMethod === "phone" ? "Email (optionnel)" : "Téléphone (optionnel)"}
                      </Label>
                      {registerMethod === "phone" ? (
                        <Input
                          id="register-email-optional"
                          type="email"
                          placeholder="votre@email.com (optionnel)"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          className={INPUT_CLASS}
                        />
                      ) : (
                        <Input
                          id="register-phone-optional"
                          type="tel"
                          placeholder="+221 77 123 45 67 (optionnel)"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          className={INPUT_CLASS}
                        />
                      )}
                    </div>

                    {/* Courier specific fields */}
                    {registerData.role === 'courier' && (
                      <div className="p-4 bg-[#0F172A] rounded-2xl border border-orange-400/30 space-y-4">
                        <h4 className="font-bold uppercase tracking-widest text-xs text-orange-400 flex items-center gap-2">
                          <Bike className="w-4 h-4" />
                          Profil Livreur / Chauffeur
                        </h4>

                        {/* Choix du métier — détermine le type de courses reçues */}
                        <div className="space-y-2">
                          <Label className="text-gray-300 text-xs uppercase tracking-widest font-bold">Type d&apos;activité *</Label>
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => setRegisterData({
                                ...registerData,
                                driver_type: 'livreur',
                                vehicle_type: registerData.vehicle_type === 'voiture' ? '' : registerData.vehicle_type,
                              })}
                              className={`p-3 rounded-xl border-2 text-left transition-all ${
                                registerData.driver_type === 'livreur'
                                  ? 'border-[#003594] bg-[#003594]/15 shadow-lg shadow-[#003594]/20'
                                  : 'border-white/10 bg-black/40 hover:border-white/30'
                              }`}
                              data-testid="register-driver-type-livreur"
                            >
                              <div className="text-xl mb-1">🛵</div>
                              <div className="text-[11px] font-black uppercase tracking-widest text-white">Livreur</div>
                              <div className="text-[9px] text-slate-400 mt-0.5">Colis, food, cargo</div>
                            </button>
                            <button
                              type="button"
                              onClick={() => setRegisterData({
                                ...registerData,
                                driver_type: 'taxi',
                                vehicle_type: 'voiture',
                              })}
                              className={`p-3 rounded-xl border-2 text-left transition-all ${
                                registerData.driver_type === 'taxi'
                                  ? 'border-[#D4AF37] bg-[#D4AF37]/15 shadow-lg shadow-[#D4AF37]/20'
                                  : 'border-white/10 bg-black/40 hover:border-white/30'
                              }`}
                              data-testid="register-driver-type-taxi"
                            >
                              <div className="text-xl mb-1">🚕</div>
                              <div className="text-[11px] font-black uppercase tracking-widest text-white">Chauffeur Taxi</div>
                              <div className="text-[9px] text-slate-400 mt-0.5">Transport personnes</div>
                            </button>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="register-vehicle" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Type de véhicule *</Label>
                          <select
                            id="register-vehicle"
                            className={SELECT_CLASS}
                            value={registerData.vehicle_type}
                            onChange={(e) => setRegisterData({ ...registerData, vehicle_type: e.target.value })}
                            required
                            data-testid="register-vehicle-select"
                          >
                            <option value="">Sélectionner...</option>
                            {registerData.driver_type === 'taxi' ? (
                              <option value="voiture">🚗 Voiture (Taxi)</option>
                            ) : (
                              <>
                                <option value="moto">🏍️ Moto</option>
                                <option value="velo">🚲 Vélo</option>
                                <option value="voiture">🚗 Voiture</option>
                              </>
                            )}
                          </select>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-orange-300 bg-orange-400/10 p-3 rounded-lg border border-orange-400/20">
                          <span className="text-lg">⚠️</span>
                          <span>Votre compte sera activé après validation par l&apos;administrateur</span>
                        </div>
                      </div>
                    )}

                    {/* Partner specific fields */}
                    {registerData.role === 'artisan' && (
                      <ArtisanSpecialtyPicker value={artisanSpecs} onChange={setArtisanSpecs} />
                    )}

                    {registerData.role === 'partner' && (
                      <div className="p-4 bg-[#0F172A] rounded-2xl border border-emerald-400/30 space-y-4">
                        <h4 className="font-bold uppercase tracking-widest text-xs text-emerald-400 flex items-center gap-2">
                          <Store className="w-4 h-4" />
                          Informations Commerce
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="register-business" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Nom de la boutique *</Label>
                            <Input
                              id="register-business"
                              placeholder="Ma Super Boutique"
                              value={registerData.business_name}
                              onChange={(e) => setRegisterData({ ...registerData, business_name: e.target.value })}
                              required
                              className={INPUT_CLASS}
                              data-testid="register-business-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="register-business-type" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Type de commerce *</Label>
                            <select
                              id="register-business-type"
                              className={SELECT_CLASS}
                              value={registerData.business_type}
                              onChange={(e) => setRegisterData({ ...registerData, business_type: e.target.value })}
                              required
                              data-testid="register-business-type-select"
                            >
                              <option value="">Sélectionner...</option>
                              <option value="boutique">🛍️ Boutique</option>
                              <option value="restaurant">🍽️ Restaurant</option>
                              <option value="commerce">🏬 Commerce général</option>
                              <option value="pharmacie">💊 Pharmacie</option>
                              <option value="supermarche">🛒 Supermarché</option>
                              <option value="autre">📦 Autre</option>
                            </select>
                            {(
                              <div className="flex items-center gap-3 pt-1" data-testid="register-restaurant-info">
                                <RestaurantInfoButton label />
                                <span className="text-[11px] text-gray-400">Restaurants : 5 000 F d'adhésion · 3 mois offerts · puis 1 000 F/mois</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Vendor/Artisan specific fields */}
                    {registerData.role === 'vendor' && (
                      <div className="p-4 bg-[#0F172A] rounded-2xl border border-[#D4AF37]/30 space-y-4">
                        <h4 className="font-bold uppercase tracking-widest text-xs text-[#D4AF37] flex items-center gap-2">
                          <Scissors className="w-4 h-4" />
                          Informations ARTISANS
                        </h4>
                        <div className="space-y-2">
                          <Label htmlFor="register-atelier" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Nom de l&apos;atelier</Label>
                          <Input
                            id="register-atelier"
                            placeholder="Atelier Couture Élégance"
                            value={registerData.business_name}
                            onChange={(e) => setRegisterData({ ...registerData, business_name: e.target.value })}
                            className={INPUT_CLASS}
                            data-testid="register-atelier-input"
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="register-address" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Adresse</Label>
                      <Input
                        id="register-address"
                        placeholder="Médina, Dakar"
                        value={registerData.address}
                        onChange={(e) => setRegisterData({ ...registerData, address: e.target.value })}
                        className={INPUT_CLASS}
                        data-testid="register-address-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-password" className="text-gray-300 text-xs uppercase tracking-widest font-bold">Mot de passe *</Label>
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        required
                        className={INPUT_CLASS}
                        data-testid="register-password-input"
                      />
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-[#D4AF37] to-[#B8962E] text-[#0F172A] hover:opacity-90 rounded-full py-6 text-sm font-black uppercase tracking-widest shadow-xl shadow-[#D4AF37]/20"
                      disabled={isLoading}
                      data-testid="register-submit-btn"
                    >
                      {isLoading ? "Inscription..." : `S'inscrire comme ${selectedType?.label}`}
                    </Button>
                  </form>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
